import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { DestinationFilter } from './DestinationPicker.js';
import { pageDestinationId, pageIdOf } from '../lib/destinations.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { InboxCard } from './InboxCard.js';
import { Icon } from './Icons.js';

/**
 * Everything waiting for a person, across every Page, in one list.
 *
 * The point is that nothing has to be chosen before anything can be done. Every
 * other screen here starts with "which Page?" — fine for one Page, punishing at
 * twenty, where each task begins with a hunt through a dropdown. Here the row
 * says which Page it is, which post it is under, and carries the reply box with
 * it.
 *
 * The Page filter narrows the list; it does not open one. That is the whole
 * difference between navigating and filtering, and it is why "all Pages" is the
 * default rather than an option.
 */
export function InboxPanel({
  onOpenConversation,
}: {
  /** Open this exchange on the messages screen. */
  onOpenConversation: (conversationId: string) => void;
}) {
  const { t, language } = useI18n();
  const locale = language === 'ar' ? 'ar' : 'en';
  const queryClient = useQueryClient();

  const [pageId, setPageId] = useState('');
  const [status, setStatus] = useState<'waiting' | 'ignored' | 'done'>('waiting');
  /*
   * What is typed, and what has been asked for.
   *
   * Kept apart so the list is not re-fetched on every keystroke. Searching runs
   * over every item this tenant has, which is the point — it is what makes a
   * customer from three weeks ago reachable at all — and that is not a query to
   * fire per character.
   */
  const [draftQuery, setDraftQuery] = useState('');
  const [query, setQuery] = useState('');
  const [managingReplies, setManagingReplies] = useState(false);
  /** Cursors for the pages already loaded, so Show more accumulates. */
  const [pages_, setPages] = useState<Array<string | undefined>>([undefined]);

  const reset = () => setPages([undefined]);

  const inbox = useQuery({
    queryKey: ['inbox', pageId, status, query, pages_.length],
    queryFn: async () => {
      /*
       * Every loaded page, re-fetched together.
       *
       * Simpler than accumulating in state, and it keeps the polling honest: a
       * reply sent on the third page updates there rather than only at the top,
       * which is what accumulating would have given.
       */
      const loaded = await Promise.all(
        pages_.map((before) =>
          api.inbox({ pageId: pageId || undefined, status, q: query || undefined, before }),
        ),
      );
      const last = loaded.at(-1);
      return {
        items: loaded.flatMap((response) => response.items),
        waiting: last?.waiting ?? 0,
        nextBefore: last?.nextBefore ?? null,
      };
    },
    // New comments arrive on their own schedule; the list should not need a
    // reload to show them.
    refetchInterval: 60_000,
  });

  const refresh = useMutation({
    mutationFn: api.refreshInbox,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });

  const items = inbox.data?.items ?? [];
  const waiting = inbox.data?.waiting ?? 0;
  const nextBefore = inbox.data?.nextBefore ?? null;

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>
          {t('inbox.title')}
          {waiting > 0 && <span className="inbox-count">{waiting}</span>}
        </h2>
        <div className="actions" style={{ marginTop: 0 }}>
          <button
            type="button"
            className="ghost small"
            onClick={() => setManagingReplies((current) => !current)}
          >
            {managingReplies ? t('common.close') : t('saved.manage')}
          </button>
          <button
            type="button"
            className="ghost small"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            <Icon.refresh className={refresh.isPending ? 'spin' : undefined} />
            {refresh.isPending ? t('inbox.checking') : t('inbox.check')}
          </button>
        </div>
      </div>

      {managingReplies && <SavedReplies />}

      <div className="filters">
        <div className="preset-row">
          <button
            type="button"
            className={status === 'waiting' ? 'subtab active' : 'subtab'}
            onClick={() => {
              setStatus('waiting');
              reset();
            }}
          >
            {t('inbox.waiting')}
          </button>
          <button
            type="button"
            className={status === 'ignored' ? 'subtab active' : 'subtab'}
            onClick={() => {
              setStatus('ignored');
              reset();
            }}
          >
            {t('inbox.ignored')}
          </button>
          <button
            type="button"
            className={status === 'done' ? 'subtab active' : 'subtab'}
            onClick={() => {
              setStatus('done');
              reset();
            }}
          >
            {t('inbox.done')}
          </button>
        </div>

        {/* Enter rather than as-you-type: this searches everything the tenant
            has, which is the point of it, and not a query to fire per key. */}
        <form
          className="inline-form"
          style={{ margin: 0 }}
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(draftQuery.trim());
            reset();
          }}
        >
          <input
            type="search"
            value={draftQuery}
            placeholder={t('inbox.searchPlaceholder')}
            onChange={(event) => setDraftQuery(event.target.value)}
          />
          <button type="submit" className="ghost small">
            {t('inbox.search')}
          </button>
          {query && (
            <button
              type="button"
              className="ghost small"
              onClick={() => {
                setDraftQuery('');
                setQuery('');
                reset();
              }}
            >
              {t('inbox.clearSearch')}
            </button>
          )}
        </form>

        <DestinationFilter
          feature={DestinationFeature.INBOX}
          value={pageId ? pageDestinationId(pageId) : ''}
          allLabel={t('inbox.allPages')}
          onChange={(id) => {
            setPageId(pageIdOf(id));
            reset();
          }}
        />
      </div>

      {inbox.isError && <ErrorNotice error={inbox.error} />}
      {inbox.isLoading && <div className="loading">{t('common.loading')}</div>}

      {inbox.data && items.length === 0 && (
        <div className="empty-state">
          <Icon.check />
          <p>
            {query
              ? t('inbox.noMatches', { query })
              : status === 'waiting'
                ? t('inbox.allClear')
                : status === 'ignored'
                  ? t('inbox.nothingIgnored')
                  : t('inbox.nothingDone')}
          </p>
        </div>
      )}

      <ul className="inbox-list">
        {items.map((item) => (
          <InboxCard
            key={item.id}
            item={item}
            locale={locale}
            readOnly={status !== 'waiting'}
            onOpenConversation={onOpenConversation}
          />
        ))}
      </ul>

      {/* The list was capped at fifty rows with no way past them, which at
          twenty Pages made it a window onto the last fifty things that
          happened and nothing else. */}
      {nextBefore && (
        <div className="actions">
          <button
            type="button"
            className="ghost"
            onClick={() => setPages((current) => [...current, nextBefore])}
            disabled={inbox.isFetching}
          >
            {inbox.isFetching ? t('common.loading') : t('inbox.showMore')}
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * Editing the sentences this tenant sends every day.
 *
 * Kept inside the inbox rather than on a settings page, because the moment
 * somebody realises they need a saved reply is the moment they have just typed
 * one for the third time — and a trip to Settings is where that realisation
 * goes to die.
 */
function SavedReplies() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const replies = useQuery({ queryKey: ['saved-replies'], queryFn: api.savedReplies });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['saved-replies'] });

  const create = useMutation({
    mutationFn: () => api.createSavedReply({ title: title.trim(), body: body.trim() }),
    onSuccess: () => {
      setTitle('');
      setBody('');
      void invalidate();
    },
  });

  const remove = useMutation({ mutationFn: api.deleteSavedReply, onSuccess: invalidate });

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{t('saved.title')}</h3>
      <p className="tiny muted">{t('saved.hint')}</p>

      {create.isError && <ErrorNotice error={create.error} />}
      {remove.isError && <ErrorNotice error={remove.error} />}

      {(replies.data?.replies.length ?? 0) > 0 && (
        <ul className="inbox-list">
          {replies.data?.replies.map((entry) => (
            <li key={entry.id} className="inbox-item">
              <div className="inbox-meta">
                <strong>{entry.title}</strong>
                {/* How often it was actually used, which answers "does this
                    earn its place" better than anybody's memory does. */}
                <span className="muted small">{t('saved.used', { count: entry.useCount })}</span>
                <button
                  type="button"
                  className="danger small"
                  style={{ marginInlineStart: 'auto' }}
                  onClick={() => remove.mutate(entry.id)}
                >
                  {t('common.delete')}
                </button>
              </div>
              <p className="inbox-message">{entry.body}</p>
            </li>
          ))}
        </ul>
      )}

      <label htmlFor="sr-title">{t('saved.name')}</label>
      <input id="sr-title" value={title} onChange={(event) => setTitle(event.target.value)} />

      <label htmlFor="sr-body">{t('saved.body')}</label>
      <textarea
        id="sr-body"
        rows={3}
        value={body}
        onChange={(event) => setBody(event.target.value)}
      />

      <button
        type="button"
        onClick={() => create.mutate()}
        disabled={!title.trim() || !body.trim() || create.isPending}
      >
        <Icon.plus />
        {t('saved.add')}
      </button>
    </div>
  );
}
